/**
 * Identité et naissance pour documents (bulletin, tableau, export, fiche).
 * Priorité : champ modifiable de la fiche MyDIT2, puis repli historique MyDIT1 (`n_*`)
 * seulement s'il est vide. On ne réécrit jamais les champs `n_*`.
 */
export type BirthPlaceSource = {
	ville_dob?: string | null
	n_ville_dob?: string | null
	pays_dob?: string | null
	n_pays_dob?: string | null
	n_dob?: string | null
	n_nationalite?: string | null
	date_naissance?: string | null
	personne?: {
		lieu_de_naissance?: string | null
		lieu_naissance?: string | null
		ville_naissance?: string | null
		date_de_naissance?: string | null
		date_naissance?: string | null
		nationalite?: string | null
	} | null
}

/** Ville de naissance : `ville_dob` (fiche) avant `n_ville_dob`. */
export function resolveBirthCity(source: BirthPlaceSource | null | undefined): string {
	if (!source) return ''
	const p = source.personne
	return (
		source.ville_dob ||
		source.n_ville_dob ||
		p?.lieu_de_naissance ||
		p?.lieu_naissance ||
		p?.ville_naissance ||
		''
	)
		.trim()
}

/** Pays de naissance : `pays_dob` (fiche), puis repli MyDIT1, puis nationalité modifiable. */
export function resolveBirthCountry(source: BirthPlaceSource | null | undefined): string {
	if (!source) return ''
	const p = source.personne
	return (
		source.pays_dob ||
		source.n_pays_dob ||
		p?.nationalite ||
		source.n_nationalite ||
		''
	)
		.trim()
}

/** Date de naissance brute (ISO ou chaîne API). MyDIT2 (`personne.date_de_naissance`) prime sur le champ historique MyDIT1 `n_dob`. */
export function resolveBirthDateRaw(source: BirthPlaceSource | null | undefined): string {
	if (!source) return ''
	const p = source.personne
	const raw =
		p?.date_de_naissance ||
		p?.date_naissance ||
		source.date_naissance ||
		source.n_dob ||
		''
	return String(raw).trim()
}

function formatDateLongFr(val: string): string {
	if (!val) return ''
	const d = new Date(val)
	if (Number.isNaN(d.getTime())) return val.trim()
	return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })
}

function formatDateShortFr(val: string): string {
	if (!val) return ''
	const d = new Date(val)
	if (Number.isNaN(d.getTime())) return val.trim()
	return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'numeric', year: '2-digit' })
}

/**
 * Bulletin / aperçu : « 01 décembre 2002 à Dakar (Sénégal) »
 */
export function formatDateLieuNaissanceBulletin(
	source: BirthPlaceSource | null | undefined
): string {
	const dateRaw = resolveBirthDateRaw(source)
	const ville = resolveBirthCity(source)
	const pays = resolveBirthCountry(source)
	const dateStr = formatDateLongFr(dateRaw)

	if (!dateStr && !ville && !pays) return ''

	const lieuPart = ville && pays ? `${ville} (${pays})` : ville || pays
	if (!dateStr) return lieuPart
	if (!lieuPart) return dateStr
	return `${dateStr} à ${lieuPart}`
}

/**
 * Tableau de délibération : « 1/12/02 à Dakar »
 */
export function formatNaissanceDeliberationTableau(
	source: BirthPlaceSource | null | undefined
): string {
	const dateRaw = resolveBirthDateRaw(source)
	const ville = resolveBirthCity(source)
	if (!dateRaw) return ville ? `à ${ville}` : ''

	const dateStr = formatDateShortFr(dateRaw)
	if (!ville) return dateStr
	return `${dateStr} à ${ville}`
}
